import { t as uiText } from '../i18n';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useHashtagContext } from '../context/HashtagContext';
import { useSearchContext } from '../context/SearchContext';
import { getWorkspaceHashtags } from '../features/hashtag/hashtagUtils';
import { HashtagData } from '../types/hashtag';
import { queryKeys } from '../lib/queryKeys';
import { fetchHashtags } from '../api/hashtags';

interface HashtagListViewProps {
  onHashtagSelect?: (hashtagName: string) => void;
}

// 安全な日付変換ユーティリティ関数
const formatSafeDate = (dateValue: any): string => {
  if (!dateValue) return '';
  
  try {
    // Firestoreの Timestamp オブジェクトの場合
    if (dateValue && typeof dateValue.toDate === 'function') {
      return new Date(dateValue.toDate()).toLocaleDateString('ja-JP');
    }
    // 既にDateオブジェクトの場合
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString('ja-JP');
    }
    // 文字列やタイムスタンプ数値の場合
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('ja-JP');
      }
    }
    return uiText("Unknown date");
  } catch (error) {
    console.warn('日付の変換に失敗:', error, dateValue);
    return uiText("Unknown date");
  }
};

const HashtagListView: React.FC<HashtagListViewProps> = ({ onHashtagSelect }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { setSelectedHashtag } = useHashtagContext();
  const { setSearchQuery, setIsSearching } = useSearchContext();
  
  // レスポンシブ対応用の状態
  const [isMobile, setIsMobile] = useState(false);

  // レスポンシブ対応のウィンドウサイズ監視
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 初期チェック
    checkIsMobile();
    
    // リサイズイベントリスナーを追加
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // データ取得関数
  const fetchHashtagsData = async (): Promise<HashtagData[]> => {
    if (!routeWorkspaceId) return [];

    const hashtagList = await fetchHashtags(routeWorkspaceId);
    
    // 使用回数でソート
    hashtagList.sort((a, b) => (b.count || 0) - (a.count || 0));
    
    return hashtagList as HashtagData[];
  };

  // TanStack Query使用
  const { 
    data: hashtags = [], 
    isLoading: loading, 
    error 
  } = useQuery({
    queryKey: queryKeys.tags.list(routeWorkspaceId ?? ''),
    queryFn: () => {
      if (import.meta.env.MODE === 'development') {}
      return fetchHashtagsData();
    },
    enabled: !!routeWorkspaceId,
  });

  // デバッグログ（開発環境のみ）
  if (import.meta.env.MODE === 'development') {}

  // 3. 実際のDOM要素の確認
  useEffect(() => {
    // ハッシュタグ一覧のコンテナ要素を確認
    const hashtagListContainer = document.querySelector('.hashtag-list-container');
    const hashtagItems = document.querySelectorAll('.hashtag-item');
  }, [hashtags, loading, error]);

  const handleHashtagClick = (hashtagName: string) => {
    // ハッシュタグを選択
    setSelectedHashtag(hashtagName);

    // 検索状態をクリア
    setSearchQuery('');
    setIsSearching(false);

    // コールバックがあれば実行
    if (onHashtagSelect) {
      onHashtagSelect(hashtagName);
    }
  };

  // エラーハンドリング
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '16px', color: '#dc3545' }}>
          {uiText("Failed to load hashtags")}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#666'
      }}>
        {uiText("Loading now...")}
      </div>
    );
  }

  if (hashtags.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: isMobile ? '20px' : '40px',
        color: '#666'
      }}>
        <p>{uiText("No hashtags yet")}</p>
        <p>{uiText("Type # followed by any text in a page to create a hashtag")}</p>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'grid', 
      gap: isMobile ? '8px' : '12px',
      marginTop: '20px'
    }}>
      {hashtags.map((hashtag) => (
        <div
          key={hashtag.id}
          onClick={() => handleHashtagClick(hashtag.name)}
          style={{
            padding: isMobile ? '12px' : '16px',
            border: '1px solid #eee',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0',
            backgroundColor: 'white'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.borderColor = 'var(--workspace-primary, #cfae91)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.borderColor = '#eee';
          }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '12px',
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : 'auto'
          }}>
            <span
              style={{
                color: 'var(--workspace-primary, #cfae91)',
                backgroundColor: 'var(--workspace-border-soft, #f5f1ed)',
                padding: isMobile ? '3px 6px' : '4px 8px',
                borderRadius: '12px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: 'bold',
                border: '1px solid var(--workspace-primary, #cfae91)',
                textAlign: isMobile ? 'center' : 'left',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              #{hashtag.name}
            </span>
            <span style={{ 
              color: '#666', 
              fontSize: isMobile ? '12px' : '14px',
              textAlign: isMobile ? 'center' : 'left',
              width: isMobile ? '100%' : 'auto'
            }}>
              {hashtag.count}{uiText(" boards")}
            </span>
          </div>
          <div style={{ 
            color: '#999', 
            fontSize: isMobile ? '10px' : '12px',
            textAlign: isMobile ? 'center' : 'right',
            width: isMobile ? '100%' : 'auto'
          }}>
{formatSafeDate(hashtag.lastUsed)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HashtagListView;





